import { useEffect, useRef, useCallback } from 'react';
import {
  Copy,
  ClipboardPaste,
  Scissors,
  Trash2,
  CopyPlus,
  ChevronsUp,
  ChevronsDown,
  ChevronUp,
  ChevronDown,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Group,
  Ungroup,
  Sparkles,
  Zap,
} from 'lucide-react';

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  selectedCount: number;
  onBringToFront: () => void;
  onBringForward: () => void;
  onSendBackward: () => void;
  onSendToBack: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
  onToggleLock: () => void;
  onToggleVisibility: () => void;
  onGroup: () => void;
  onUngroup: () => void;
  onMergeToSvg: () => void;
  onExplode: () => void;
  isLocked: boolean;
  isVisible: boolean;
  canPaste: boolean;
  canGroup: boolean;
  canUngroup: boolean;
  canMerge: boolean;
  canExplode: boolean;
}

interface MenuItem {
  label: string;
  icon: React.ElementType;
  shortcut?: string;
  action: () => void;
  danger?: boolean;
  disabled?: boolean;
  dividerAfter?: boolean;
}

export function CanvasContextMenu({
  x,
  y,
  onClose,
  selectedCount,
  onBringToFront,
  onBringForward,
  onSendBackward,
  onSendToBack,
  onDuplicate,
  onDelete,
  onCopy,
  onCut,
  onPaste,
  onToggleLock,
  onToggleVisibility,
  onGroup,
  onUngroup,
  onMergeToSvg,
  onExplode,
  isLocked,
  isVisible,
  canPaste,
  canGroup,
  canUngroup,
  canMerge,
  canExplode,
}: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        close();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const handleScroll = () => close();

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 0);
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [close]);

  useEffect(() => {
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (rect.right > vw) {
      menuRef.current.style.left = `${x - rect.width}px`;
    }
    if (rect.bottom > vh) {
      menuRef.current.style.top = `${y - rect.height}px`;
    }
  }, [x, y]);

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const mod = isMac ? '\u2318' : 'Ctrl+';

  const items: MenuItem[] = [];

  if (selectedCount > 0) {
    items.push(
      { label: 'Copy', icon: Copy, shortcut: `${mod}C`, action: onCopy },
      { label: 'Cut', icon: Scissors, shortcut: `${mod}X`, action: onCut },
    );
  }
  items.push({
    label: 'Paste',
    icon: ClipboardPaste,
    shortcut: `${mod}V`,
    action: onPaste,
    disabled: !canPaste,
    dividerAfter: true,
  });

  if (selectedCount > 0) {
    items.push({
      label: 'Duplicate',
      icon: CopyPlus,
      shortcut: `${mod}D`,
      action: onDuplicate,
      dividerAfter: true,
    });

    items.push(
      { label: 'Bring to Front', icon: ChevronsUp, shortcut: `${mod}]`, action: onBringToFront },
      { label: 'Bring Forward', icon: ChevronUp, shortcut: ']', action: onBringForward },
      { label: 'Send Backward', icon: ChevronDown, shortcut: '[', action: onSendBackward },
      { label: 'Send to Back', icon: ChevronsDown, shortcut: `${mod}[`, action: onSendToBack, dividerAfter: true },
    );

    if (canGroup) {
      items.push({ label: 'Group', icon: Group, shortcut: `${mod}G`, action: () => { console.log('[GROUP-ENTRY-CONTEXTMENU]', { stage: 'context-menu-group-clicked', timestamp: Date.now() }); onGroup(); } });
    }
    if (canUngroup) {
      items.push({ label: 'Ungroup', icon: Ungroup, shortcut: `${mod}\u21E7G`, action: () => { console.log('[UNGROUP-ENTRY-CONTEXTMENU]', { stage: 'context-menu-ungroup-clicked', timestamp: Date.now() }); onUngroup(); } });
    }
    if (canMerge) {
      items.push({ label: 'Merge to SVG', icon: Sparkles, action: onMergeToSvg });
    }
    if (canExplode) {
      items.push({ label: 'Explode to Elements', icon: Zap, action: onExplode });
    }
    if (canGroup || canUngroup || canMerge || canExplode) {
      items[items.length - 1].dividerAfter = true;
    }

    items.push(
      { label: isLocked ? 'Unlock' : 'Lock', icon: isLocked ? Unlock : Lock, action: onToggleLock },
      {
        label: isVisible ? 'Hide' : 'Show',
        icon: isVisible ? EyeOff : Eye,
        action: onToggleVisibility,
        dividerAfter: true,
      },
    );

    items.push({
      label: selectedCount > 1 ? `Delete ${selectedCount} elements` : 'Delete',
      icon: Trash2,
      shortcut: 'Del',
      action: onDelete,
      danger: true,
    });
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-[100] min-w-[200px] py-1 bg-[#1c1c1c] border border-white/10 rounded-lg shadow-2xl animate-fade-in"
      style={{ left: x, top: y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, idx) => {
        const Icon = item.icon;
        return (
          <div key={`${item.label}-${idx}`}>
            <button
              onClick={() => {
                if (!item.disabled) {
                  item.action();
                  onClose();
                }
              }}
              disabled={item.disabled}
              className={`
                w-full flex items-center gap-2.5 px-3 py-1.5 text-xs
                transition-colors duration-75
                ${item.disabled
                  ? 'text-zinc-600 cursor-not-allowed'
                  : item.danger
                    ? 'text-red-400 hover:bg-red-500/10'
                    : 'text-zinc-300 hover:bg-white/[0.08] hover:text-white'
                }
              `}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 text-left">{item.label}</span>
              {item.shortcut && (
                <span className="text-[10px] text-zinc-600 ml-4 font-mono">{item.shortcut}</span>
              )}
            </button>
            {item.dividerAfter && <div className="my-1 border-t border-white/[0.06]" />}
          </div>
        );
      })}
    </div>
  );
}
